import { compact, isEmpty, isNumber } from 'lodash';

import { RECORDS_PER_PAGE, SORT_DIRECTION } from '@app/constants';

import {
  ALL_RECORDS_CQL,
  SEARCH_OPTION,
  SearchState,
  SORT_FIELD,
  sortFieldMap,
} from '../../constants';
import { getQueryCQL } from '../getQueryCQL';
import { getPublicationYearCQL } from '../getPublicationYearCQL';
import { getFacetsCQL } from '../getFacetsCQL';

export interface SQLState extends SearchState {
  hasRequestedFacets?: boolean;
}

const getCQLSearchQuery = ({
  query = '',
  option = SEARCH_OPTION.keyword,
  sortField = SORT_FIELD.relevance,
  sortDirection = SORT_DIRECTION.asc,
  facets,
  publicationYear,
  hasRequestedFacets,
}: SQLState) => {
  if (
    query === '' &&
    !hasRequestedFacets &&
    isEmpty(facets) &&
    isEmpty(publicationYear)
  ) {
    return '';
  }

  const sortByParam =
    sortField !== SORT_FIELD.relevance
      ? ` sortBy ${sortFieldMap[sortField]}/sort.${sortDirection}`
      : '';

  const isRequestForFacetAttributes =
    hasRequestedFacets &&
    query === '' &&
    isEmpty(facets) &&
    isEmpty(publicationYear);

  if (isRequestForFacetAttributes) {
    return ALL_RECORDS_CQL;
  }

  const facetCQL = getFacetsCQL(facets);
  const publicationYearCQL = getPublicationYearCQL(publicationYear);
  const queryCQL = getQueryCQL(query, option);
  const queryParamWithFacetParam = compact([
    queryCQL,
    facetCQL,
    publicationYearCQL,
  ]).join(' and ');

  return `${queryParamWithFacetParam}${sortByParam}`;
};

interface SQLQuery {
  query: string;
  limit: number;
  offset?: number;
}

export const getCQLState = ({
  query,
  option,
  sortField,
  sortDirection,
  facets,
  publicationYear,
  pageNumber,
  hasRequestedFacets,
  recordsPerPage = RECORDS_PER_PAGE,
  limit = recordsPerPage,
  offset,
}: SQLState): SQLQuery => {
  const offsetValue =
    offset ??
    (isNumber(pageNumber) &&
      pageNumber > 1 &&
      recordsPerPage * (pageNumber - 1));

  return {
    query: getCQLSearchQuery({
      query,
      option,
      sortField,
      sortDirection,
      facets,
      publicationYear,
      hasRequestedFacets,
    }),
    limit,
    ...(offsetValue && {
      offset: offsetValue,
    }),
  };
};
